import { useEffect, useRef, useCallback } from 'react';
import { AdapterDateFnsJalali } from '@mui/x-date-pickers/AdapterDateFnsJalali';
import { toEnglishDigits, toPersianDigits } from '../utils/convertNumbers';

// Custom adapter that extends AdapterDateFnsJalali to display Persian digits and weekdays
export class CustomAdapterDateFnsJalali extends AdapterDateFnsJalali {
  format(date, formatString) {
    const formattedDate = super.format(date, formatString);
    return toPersianDigits(formattedDate);
  }

  // Some MUI internals use formatByString instead of format()
  formatByString(date, formatString) {
    const formattedDate = super.formatByString(date, formatString);
    return toPersianDigits(formattedDate);
  }

  formatNumber(number) {
    return toPersianDigits(super.formatNumber(number));
  }

  // Use full Persian month names in month selection views (MMMM instead of MMM)
  getMonthText(date) {
    return toPersianDigits(super.format(date, 'MMMM'));
  }

  // Ensure year labels use Persian digits (e.g. ۱۴۰۴)
  getYearText(date) {
    return toPersianDigits(super.getYearText(date));
  }

  // Override getWeekdays to return Persian weekday names
  getWeekdays() {
    return ['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج']; // Saturday to Friday in Persian
  }

  // Override getWeekdayArray to return Persian weekday names
  getWeekdayArray() {
    return ['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج']; // Saturday to Friday in Persian
  }

  // Override getWeekdayNames to return Persian weekday names
  getWeekdayNames() {
    return ['شنبه', 'یکشنبه', 'دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنج‌شنبه', 'جمعه'];
  }

  // Override getWeekdayNamesShort to return Persian weekday abbreviations
  getWeekdayNamesShort() {
    return ['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج'];
  }
}

// Hook for Persian date picker DOM manipulation
export const usePersianDatePicker = () => {
  const observerRef = useRef(null);

  const isDebugEnabled = () => {
    try {
      return typeof window !== 'undefined' && window.localStorage?.getItem('PERSIAN_PICKER_DEBUG') === '1';
    } catch {
      return false;
    }
  };

  const convertYearLabels = useCallback(() => {
    // Year selection grid labels (e.g. 1404) should be Persian digits (۱۴۰۴)
    const yearSelectors = [
      '.MuiYearCalendar-root .MuiPickersYear-yearButton',
      '.MuiYearCalendar-root button',
      '.MuiYearCalendar-root [role="radio"]',
      '[class*="YearCalendar"] button',
      '[class*="YearCalendar"] [role="radio"]',
    ];

    yearSelectors.forEach((selector) => {
      const els = document.querySelectorAll(selector);
      els.forEach((el) => {
        const raw = el.textContent?.trim();
        if (!raw) return;
        if (!/\d/.test(raw) && !/[۰-۹٠-٩]/.test(raw)) return;

        const converted = toPersianDigits(raw);
        if (converted !== raw) {
          if (isDebugEnabled()) {
            console.debug('[PersianPicker] year:', raw, '->', converted);
          }
          el.textContent = converted;
        }
      });
    });
  }, []);

  const convertWeekdays = useCallback(() => {
    // Convert weekday header labels inside MUI pickers.
    // NOTE: We intentionally avoid ".MuiDayCalendar-weekContainer" (it is a week row of days, not header).
    const weekdayRowSelectors = [
      '.MuiDayCalendar-header',
      '.MuiDayCalendar-weekDayLabel',
      '.MuiPickersCalendarHeader-root ~ .MuiDayCalendar-header',
      '.MuiPickersCalendarHeader-root + .MuiDayCalendar-header',
    ];

    const weekdayMap = {
      // Numeric indices (Sat..Fri) - English, Persian, Arabic-Indic
      '0': 'ش', '1': 'ی', '2': 'د', '3': 'س', '4': 'چ', '5': 'پ', '6': 'ج',
      '۰': 'ش', '۱': 'ی', '۲': 'د', '۳': 'س', '۴': 'چ', '۵': 'پ', '۶': 'ج',
      '٠': 'ش', '١': 'ی', '٢': 'د', '٣': 'س', '٤': 'چ', '٥': 'پ', '٦': 'ج',

      // Common abbreviations from various locales
      'S': 'ش', 'Sa': 'ش', 'Sat': 'ش', 'Saturday': 'ش',
      'Su': 'ی', 'Sun': 'ی', 'Sunday': 'ی',
      'M': 'د', 'Mo': 'د', 'Mon': 'د', 'Monday': 'د',
      'Tu': 'س', 'Tue': 'س', 'Tuesday': 'س',
      'W': 'چ', 'We': 'چ', 'Wed': 'چ', 'Wednesday': 'چ',
      'Th': 'پ', 'Thu': 'پ', 'Thursday': 'پ',
      'F': 'ج', 'Fr': 'ج', 'Fri': 'ج', 'Friday': 'ج',
    };

    const normalizeKey = (raw) => {
      const text = String(raw || '').trim();
      if (!text) return '';
      // If it's a single non-English digit (like '۱'), normalize to English digit ('1')
      if (text.length <= 2 && /[۰-۹٠-٩]/.test(text)) {
        return toEnglishDigits(text);
      }
      return text;
    };

    // Try all selectors and convert any matching labels.
    weekdayRowSelectors.forEach((selector) => {
      const rows = document.querySelectorAll(selector);
      rows.forEach((row) => {
        const weekdayElements = row.querySelectorAll('div, span, .MuiTypography-root, *');
        weekdayElements.forEach((el) => {
          const raw = el.textContent?.trim();
          if (!raw) return;

          // Most weekday labels are 1-3 chars ("Sat", "Su", "1") — keep it conservative.
          if (raw.length > 10) return;

          const key = normalizeKey(raw);
          const persianWeekday = weekdayMap[key] || weekdayMap[raw];
          if (persianWeekday && el.textContent !== persianWeekday) {
            if (isDebugEnabled()) {
              console.debug('[PersianPicker] weekday:', raw, '->', persianWeekday);
            }
            el.textContent = persianWeekday;
          }
        });
      });
    });
  }, []);

  const convertMonthLabels = useCallback(() => {
    // Month selection grid usually uses short labels (e.g. "فرو"). Convert to full month names.
    const monthMap = {
      'فرو': 'فروردین',
      'ارد': 'اردیبهشت',
      'خرد': 'خرداد',
      'تیر': 'تیر',
      'مرد': 'مرداد',
      'شهر': 'شهریور',
      'مهر': 'مهر',
      'آبا': 'آبان',
      'آذر': 'آذر',
      'دی': 'دی',
      'بهم': 'بهمن',
      'اسف': 'اسفند',
    };

    const monthButtonSelectors = [
      '.MuiPickersMonth-monthButton',
      '.MuiMonthCalendar-root button',
      '.MuiMonthCalendar-root [role="radio"]',
      '[class*="MonthCalendar"] button',
      '[class*="MonthCalendar"] [role="radio"]',
    ];

    monthButtonSelectors.forEach((selector) => {
      const els = document.querySelectorAll(selector);
      els.forEach((el) => {
        const raw = el.textContent?.trim();
        if (!raw) return;
        const full = monthMap[raw];
        if (full && raw !== full) {
          if (isDebugEnabled()) {
            console.debug('[PersianPicker] month:', raw, '->', full);
          }
          el.textContent = full;
        }
      });
    });
  }, []);

  const convertAllElements = useCallback(() => {
    // Convert calendar day numbers
    const dayElements = document.querySelectorAll('.MuiPickersDay-root');
    dayElements.forEach((dayEl) => {
      const dayNumber = dayEl.textContent?.trim();
      if (dayNumber && /^\d+$/.test(dayNumber)) {
        const persianDay = toPersianDigits(dayNumber);
        dayEl.textContent = persianDay;
      }
    });

    // Convert weekday names
    convertWeekdays();

    // Convert month labels (month picker view)
    convertMonthLabels();

    // Convert year labels (year picker view)
    convertYearLabels();

    // Convert year numbers in header
    const yearSelectors = [
      '.MuiPickersCalendarHeader-label',
      '.MuiPickersCalendarHeader-switchViewButton',
      '.MuiPickersCalendarHeader-root .MuiTypography-root'
    ];
    
    yearSelectors.forEach(selector => {
      const yearElements = document.querySelectorAll(selector);
      yearElements.forEach((yearEl) => {
        const yearText = yearEl.textContent;
        if (yearText && /\d/.test(yearText)) {
          const persianYear = toPersianDigits(yearText);
          if (isDebugEnabled() && persianYear !== yearText) {
            console.debug('[PersianPicker] header:', yearText, '->', persianYear);
          }
          yearEl.textContent = persianYear;
        }
      });
    });
  }, [convertWeekdays, convertMonthLabels, convertYearLabels]);

  useEffect(() => {
    // Initial conversion
    convertAllElements();
    
    // Add aggressive conversion with timer
    let conversionCount = 0;
    const aggressiveConversion = setInterval(() => {
      convertWeekdays();
      convertMonthLabels();
      convertYearLabels();
      conversionCount++;
      if (conversionCount >= 5) { // Stop after 5 attempts
        clearInterval(aggressiveConversion);
      }
    }, 200);
    
    // Watch for new calendar elements
    const observer = new MutationObserver((mutations) => {
      let shouldConvert = false;
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            if (node.classList?.contains('MuiPickersDay-root') || 
                node.querySelector?.('.MuiPickersDay-root') ||
                node.querySelector?.('.MuiPickersCalendarHeader') ||
                node.querySelector?.('.MuiDayCalendar-weekContainer') ||
                node.querySelector?.('.MuiPickersCalendarHeader-root') ||
                // Month/Year selection views
                node.classList?.contains('MuiYearCalendar-root') ||
                node.querySelector?.('.MuiYearCalendar-root') ||
                node.classList?.contains('MuiMonthCalendar-root') ||
                node.querySelector?.('.MuiMonthCalendar-root') ||
                node.querySelector?.('.MuiPickersYear-root') ||
                node.querySelector?.('.MuiPickersMonth-root')) {
              shouldConvert = true;
            }
          }
        });
      });
      
      if (shouldConvert) {
        setTimeout(convertAllElements, 10);
        setTimeout(convertAllElements, 100);
        setTimeout(convertWeekdays, 50);
        setTimeout(convertWeekdays, 150);
        setTimeout(convertMonthLabels, 50);
        setTimeout(convertMonthLabels, 150);
        setTimeout(convertYearLabels, 50);
        setTimeout(convertYearLabels, 150);
      }
    });
    
    observer.observe(document.body, { childList: true, subtree: true });
    observerRef.current = observer;
    
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      clearInterval(aggressiveConversion);
    };
  }, [convertAllElements, convertWeekdays, convertMonthLabels, convertYearLabels]);

  return { convertWeekdays, convertAllElements, convertMonthLabels, convertYearLabels };
};
